import React, { useState, useEffect } from 'react';
import { Grid, Box, Typography, CircularProgress } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useGetList } from 'react-admin';
import { v4 as uuidv4 } from 'uuid';
import { RuleKanbanBoard, RuleKanbanData, Rule, RuleGroup } from '../components/rules';

interface FormConditionKanbanProps {
  fuels: Array<{ id: string; name: string }>;
  chain?: any;
  formData?: any;
  onLoadingChange?: (loading: boolean) => void;
}

const FormConditionKanban: React.FC<FormConditionKanbanProps> = ({ fuels, chain, onLoadingChange }) => {
  const { watch, setValue } = useFormContext();

  // Pegar giftRules do form (usado em edição)
  const giftRules = watch('giftRules') || [];
  const existingRuleGroups = watch('ruleGroups') || [];

  const { data: stationsData, isLoading: stationsLoading } = useGetList('stations', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
  });

  // Parceiros (place.type='partner') entram junto com os postos: as regras de
  // compra valem tanto na conveniência do posto quanto no parceiro integrado, e
  // o recurso 'stations' do backend filtra type='station'.
  const { data: partnersData, isLoading: partnersLoading } = useGetList('chains/chainId/partners', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
  });

  const isLoadingPlaces = stationsLoading || partnersLoading;

  // Notificar mudanças no loading para o componente pai
  useEffect(() => {
    onLoadingChange?.(isLoadingPlaces);
  }, [isLoadingPlaces, onLoadingChange]);

  const stations = React.useMemo(() => {
    const fromStations = stationsData
      ? Object.values(stationsData).map((station: any) => ({
          id: station.id,
          name: station.name,
        }))
      : [];

    const fromPartners = partnersData
      ? Object.values(partnersData).map((partner: any) => ({
          id: partner.id,
          name: `${partner.name} (parceiro)`,
          // Parceiro não tem bomba: o RuleModal só o oferece em regra de compra.
          isPartner: true,
        }))
      : [];

    // Dedup defensivo: se algum place aparecer nas duas listas, mantém o primeiro
    const byId = new Map<string, { id: string; name: string; isPartner?: boolean }>();
    [...fromStations, ...fromPartners].forEach(place => {
      if (!byId.has(place.id)) byId.set(place.id, place);
    });

    return Array.from(byId.values());
  }, [stationsData, partnersData]);

  // Converter giftRules (backend) para ruleGroups (Kanban) se necessário
  const convertGiftRulesToKanban = (rules: any[]): RuleGroup[] => {
    if (rules.length === 0) return [];

    // Agrupar regras por groupId
    const groupsMap = new Map<number, Rule[]>();
    rules.forEach((rule) => {
      const groupId = rule.groupId ?? 0;
      if (!groupsMap.has(groupId)) {
        groupsMap.set(groupId, []);
      }
      groupsMap.get(groupId)!.push(rule);
    });

    // Converter para RuleGroup[] mantendo ordem
    const sortedGroupIds = Array.from(groupsMap.keys()).sort((a, b) => a - b);
    return sortedGroupIds.map((groupId) => ({
      id: uuidv4(),
      rules: groupsMap.get(groupId)!,
    }));
  };

  const initialRuleGroups = existingRuleGroups.length > 0
    ? existingRuleGroups
    : convertGiftRulesToKanban(giftRules);

  const [kanbanData, setKanbanData] = useState<RuleKanbanData>({
    ruleGroups: initialRuleGroups,
  });

  // Sincronizar com react-hook-form (ambas as estruturas)
  useEffect(() => {
    // Salvar estrutura Kanban (para edição interna)
    setValue('ruleGroups', kanbanData.ruleGroups);

    // Converter e salvar como giftRules (para backend)
    const giftRules: any[] = [];
    kanbanData.ruleGroups.forEach((group, groupIndex) => {
      group.rules.forEach((rule) => {
        giftRules.push({
          ...rule,
          groupId: groupIndex,
        });
      });
    });
    setValue('giftRules', giftRules);
  }, [kanbanData, setValue]);

  const handleChange = (newData: RuleKanbanData) => {
    setKanbanData(newData);
  };

  return (
    <Box sx={{ py: 2 }}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Configuração de Regras
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Defina as condições que o cliente precisa cumprir para ganhar o brinde.
            Organize as regras em grupos usando a lógica <strong>E</strong> entre grupos e <strong>OU</strong> dentro de cada grupo.
          </Typography>
        </Grid>

        <Grid size={12}>
          {isLoadingPlaces ? (
            <Box display="flex" alignItems="center" gap={2} sx={{ p: 2, border: '1px dashed rgba(0,0,0,0.08)', borderRadius: 1 }}>
              <CircularProgress size={20} />
              <Box>
                <Typography variant="subtitle2">Carregando postos e parceiros...</Typography>
                <Typography variant="body2" color="text.secondary">Aguarde enquanto carregamos a lista de locais disponíveis.</Typography>
              </Box>
            </Box>
          ) : (
            <RuleKanbanBoard
              data={kanbanData}
              onChange={handleChange}
              fuels={fuels}
              stations={stations}
              chain={chain}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default React.memo(FormConditionKanban);
